import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { useDirtyGuard } from "../../context/DirtyGuard";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { LeavePolicyFields } from "../admin/branch/LeavePolicyFields";
import { myCompany, updateCompany } from "../../api/directory";
import { clearCompanyCache } from "../../api/useMyCompany";

const DEFAULTS = {
  monthly_hours_target: 0,
  annual_leave_days: 30,
  annual_leave_paid: true,
  allow_unpaid_leave: true,
  min_notice_days: 3,
  carry_over_days: 0,
  eligibility_after_months: 6,
  emergency_leave_days_year: 5,
  sick_leave_days_year: 15,
  sick_requires_certificate: true,
};

// Company-wide leave policy: the default every branch inherits unless it overrides it.
export const CompanyLeavePolicy = () => {
  const { t } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    myCompany()
      .then((data) => {
        const merged = { ...DEFAULTS, ...(data.leave_policy || {}) };
        setPolicy(merged);
        setSaved(merged);
      })
      .catch(() => setPolicy(null));
  }, []);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      await updateCompany({ leave_policy: policy });
      clearCompanyCache();
      setSaved(policy);
      toast.success(t("company.saved"));
    } catch {
      toast.error(t("errors.generic"));
      throw new Error("save_failed");
    } finally {
      setBusy(false);
    }
  }, [policy, t]);

  useDirtyGuard(Boolean(policy && saved && JSON.stringify(policy) !== JSON.stringify(saved)), save);

  if (!policy) return null;

  return (
    <NeuCard data-testid="company-leave-policy">
      <p className="label-xs flex items-center gap-2">
        <CalendarCheck className="h-4 w-4 text-primary" /> {t("branches.companyPolicy")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{t("branches.companyPolicyHint")}</p>

      <div className="mt-5">
        <LeavePolicyFields value={policy} onChange={(key, v) => setPolicy({ ...policy, [key]: v })} />
      </div>

      <NeuButton variant="primary" className="mt-5" data-testid="company-leave-save" disabled={busy} onClick={save}>
        {t("app.save")}
      </NeuButton>
    </NeuCard>
  );
};
